import { useState, useCallback, useMemo, memo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { css } from 'styled-components';
import { useQuery } from '@apollo/client';
import type { ColumnDef } from '@tanstack/react-table';
import AddCircle from '@mui/icons-material/AddCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from 'common/ui/Tooltip';
import Dialog from '@mui/material/Dialog';
import { Stack } from 'common/ui/Layout';
import GenericSnackBar from 'common/GenericSnackBar/GenericSnackBar';

import { Button } from 'common/ui/Button';
import { Colors, Spacings } from 'styles';
import Text from 'common/ui/Text';
import Input from 'common/ui/Input';
import { GET_ALL_HANGING_PROTOCOLS } from 'modules/Apollo/queries';

import { Table } from 'common/Table';
import { toLocaleString } from 'utils/date';
import { DraggableContainer } from 'domains/viewer/DraggableContainer';
import {
  useHangingProtocol,
  invalidateHPInitializationCache,
  invalidateHPSmidGuard,
  markHangingProtocolDeleted,
  broadcastHPInvalidation,
} from 'domains/viewer/HangingProtocol/useHangingProtocol';
import {
  usePreviewHangingProtocol,
  usePreviewHangingProtocolConfiguration,
  useConfirmedHangingProtocolConfiguration,
} from 'domains/viewer/HangingProtocol/usePreviewHangingProtocol';
import type { Layouts } from '../../StudyLoader/viewerLoaderState';
import type {
  GetAllHangingProtocolsQuery,
  GetAllHangingProtocolsQueryVariables,
  GetHangingProtocolMatchQuery,
} from 'generated/graphql';
import { useOnUnload } from 'hooks/useOnUnload';
import { BaseWidget, AccessoryButton } from '../BaseWidget';
import { Menu, MenuItem } from 'common/ui/Menu';
import MoreVert from '@mui/icons-material/MoreVert';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { logger } from 'modules/logger';
import BorderColor from '@mui/icons-material/BorderColor';
import Replay from '@mui/icons-material/Replay';
import Refresh from '@mui/icons-material/Refresh';

import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import { HPConfirmationModal } from 'domains/viewer/HangingProtocol/Steps/HPConfirmationModal';
import { RESET_ALL_MODAL } from 'domains/viewer/HangingProtocol/Steps/stepsModalFactory';
import { RESET_ALL_TOAST } from 'domains/viewer/HangingProtocol/Steps/stepsToastFactory';
import {
  createStepToastContentComponent,
  getDefaultHPToastOptions,
} from 'domains/viewer/HangingProtocol/Steps/StepToast';
import { useClinicalStepOperations } from 'domains/viewer/HangingProtocol/hooks/useClinicalStepOperations';
import { useClinicalViewportEditing } from 'domains/viewer/HangingProtocol/hooks/useClinicalViewportEditing';
import { HangingProtocolsForm, type HangingProtocolsFormProps } from './HangingProtocolsForm';
import { HangingProtocolsForm as HangingProtocolsFormV4 } from './HPv4/HangingProtocolsFormV4';
import { useSubmitForm } from './hooks/useSubmitForm';
import {
  POWERED_BY_SIRONA_CLASSIFICATIONS,
  SNACKBAR_CONTENT_PROPS,
  HP_MODAL_VARIANTS,
  HANGING_PROTOCOL_PHASE,
  isAtLeastPhase4,
} from './constants';
import type { WidgetMode, SubmitFormIntent, HangingProtocolPhase } from './constants';
import { getSuccessNotification } from './utils/getSuccessNotification';
import { getHangingProtocolErrorMessage } from './utils/getHangingProtocolErrorMessage';
import { DeleteHPModal } from './DeleteHPModal';
import { PreviewCell } from './PreviewCell';
import { Row } from './Row';
import { useClearSteppedHPState } from 'domains/viewer/HangingProtocol/hooks/useClearSteppedHPState';
import { useDeleteHangingProtocol } from './hooks/useDeleteHangingProtocol';
import { FF, useSplitFlag } from 'modules/feature-flags';
import { Sidebar, useCloseSidebar, useSidebarState } from '../../Sidebar';
import { useBaseViewerData } from '../../StudyLoader/useStudies';
import { useViewerId } from 'hooks/useViewerId';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { telemetryContextFor } from 'modules/Apollo/types';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { rootTheme } from 'theme/rootTheme';
import { useNumberOfPriors } from './utils/useNumberOfPriors';
import { getPayloadStudyIds } from './utils/getPayloadStudyIds';

export const HANGING_PROTOCOL_SIDEBAR_ID = 'hanging-protocol';
export const HP_URL_PARAM_KEY = 'hp';
export enum HPUrlParam {
  Create = 'create',
  Edit = 'edit',
}

type HangingProtocolItem = GetAllHangingProtocolsQuery['hangingProtocols']['items'][number];

const menuItemCss = css`
  height: 24px;
  line-height: ${Spacings.xxsmall}rem;
`;

const iconCss = css`
  margin-right: ${Spacings.small}rem;
  color: ${Colors.gray10};
  opacity: 0.5;
  width: 16px;
  height: 16px;
`;

const isSubmitFormIntent = (mode: WidgetMode): mode is SubmitFormIntent =>
  mode === HP_MODAL_VARIANTS.SAVE || mode === HP_MODAL_VARIANTS.UPDATE;

type HangingProtocolWidgetProps = Readonly<{
  layouts: Layouts;
  studyDescriptions: Array<string>;
  viewerRef: {
    current: null | HTMLDivElement;
  };
}>;

const isDefault = (
  hangingProtocol: Nullable<
    GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies']
  >
) => hangingProtocol?.smid == null;

const HangingProtocolWidget = ({
  layouts,
  studyDescriptions,
  viewerRef,
}: HangingProtocolWidgetProps): React.ReactElement => {
  const [phase] = useSplitFlag<HangingProtocolPhase>(FF.HANGING_PROTOCOL_PHASE);
  const isPhase4 = isAtLeastPhase4(phase);
  const [searchParams, setSearchParams] = useSearchParams();
  const [widgetMode, setWidgetMode] = useState<WidgetMode>(null);
  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);

  const { currentCase } = useCurrentCaseContext();
  const primaryStudy = currentCase?.studies[0];
  const numberOfPriors = useNumberOfPriors({ context: APOLLO_QUERY_CONTEXT });

  const groupStudyIds = useMemo(() => getPayloadStudyIds(currentCase), [currentCase]);

  const { hangingProtocol, loadingHangingProtocol, resetToHP } = useHangingProtocol({
    context: APOLLO_QUERY_CONTEXT,
  });
  const { data: allHangingProtocols, loading } = useQuery<
    GetAllHangingProtocolsQuery,
    GetAllHangingProtocolsQueryVariables
  >(GET_ALL_HANGING_PROTOCOLS, {
    // don't fetch HPs if the widget is not open
    skip: !isOpen,
    context: APOLLO_QUERY_CONTEXT,
  });

  const previewedHangingProtocol = usePreviewHangingProtocolConfiguration();
  const confirmedHangingProtocol = useConfirmedHangingProtocolConfiguration();
  const {
    previewHangingProtocol,
    confirmPreview,
    confirmCreatedHangingProtocol,
    clearPreviewState,
    rejectAndRemovePreview,
    clearConfirmedHangingProtocol,
    loadingPreview,
  } = usePreviewHangingProtocol({ context: APOLLO_QUERY_CONTEXT });

  const closeSidebar = useCloseSidebar();
  const { isOpen: sidebarIsOpen, activeSidebarId } = useSidebarState();
  const viewingViewportClassifications =
    sidebarIsOpen && activeSidebarId === HANGING_PROTOCOL_SIDEBAR_ID;
  const showSidebarOverlay = viewingViewportClassifications && !isPhase4;

  // Track whether the widget was opened via URL param so the effect can close it
  // when the param is removed (e.g. browser back/forward) without interfering with
  // SEARCH/DELETE modes that don't use URL params.
  const openedViaUrlParam = useRef(false);

  // Derive widget state from URL param (e.g. browser back/forward, direct link)
  useEffect(() => {
    const hpParam = searchParams.get(HP_URL_PARAM_KEY);
    if (hpParam === HPUrlParam.Edit) {
      openedViaUrlParam.current = true;
      setWidgetMode(HP_MODAL_VARIANTS.UPDATE);
      setOpen(true);
    } else if (hpParam === HPUrlParam.Create) {
      openedViaUrlParam.current = true;
      setWidgetMode(HP_MODAL_VARIANTS.SAVE);
      setOpen(true);
    } else if (hpParam == null && openedViaUrlParam.current) {
      openedViaUrlParam.current = false;
      setWidgetMode(null);
      setOpen(false);
    }
  }, [searchParams]);

  const open = useCallback(
    (mode: WidgetMode) => {
      setWidgetMode(mode);
      setOpen(true);

      // Add hp param to URL when entering edit/create mode
      const hpParam =
        mode === HP_MODAL_VARIANTS.UPDATE
          ? HPUrlParam.Edit
          : mode === HP_MODAL_VARIANTS.SAVE
            ? HPUrlParam.Create
            : null;

      if (hpParam) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(HP_URL_PARAM_KEY, hpParam);
            return next;
          },
          { replace: true }
        );
      }
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    if (!isOpen || primaryStudy == null) {
      return;
    }

    if (phase === HANGING_PROTOCOL_PHASE.THREE || isPhase4) {
      closeSidebar();
    }

    // Remove hp param from URL when closing
    if (searchParams.has(HP_URL_PARAM_KEY)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(HP_URL_PARAM_KEY);
          return next;
        },
        { replace: true }
      );
    }

    setWidgetMode(null);
    setOpen(false);
  }, [closeSidebar, isOpen, phase, isPhase4, primaryStudy, searchParams, setSearchParams]);

  const [completedMessage, setCompletedMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(null);
  const unsetError = useCallback(() => setErrorMessage(null), []);

  const [highlightRow, setHighlightRow] = useState<number | null>(null);
  const columns = useMemo(
    (): ColumnDef<HangingProtocolItem>[] => [
      {
        header: 'Date',
        accessorFn: ({ created }) => toLocaleString(created),
        size: 100,
        maxSize: 100,
        minSize: 100,
      },
      {
        header: 'Name',
        accessorKey: 'name',
        size: 161,
        maxSize: 161,
        minSize: 161,
      },
      {
        header: () => <div>Actions</div>,
        accessorKey: 'actions',
        size: 134,
        maxSize: 134,
        minSize: 134,
        cell: ({ row }) => (
          <PreviewCell
            row={row}
            hangingProtocol={hangingProtocol}
            previewHangingProtocol={previewHangingProtocol}
            setHighlightRow={setHighlightRow}
            setIsPreviewAccepted={setIsPreviewAccepted}
            isLoadingPreview={loadingPreview}
          />
        ),
      },
    ],
    [hangingProtocol, loadingPreview, previewHangingProtocol]
  );

  const searchItems = useMemo(
    () =>
      widgetMode === HP_MODAL_VARIANTS.SEARCH
        ? (allHangingProtocols?.hangingProtocols?.items || []).filter(
            (item): item is typeof item & { created: NonNullable<typeof item.created> } =>
              item.name.toLowerCase().includes(search.trim().toLowerCase()) && item.created != null
          )
        : [],
    [allHangingProtocols?.hangingProtocols?.items, widgetMode, search]
  );

  useOnUnload(rejectAndRemovePreview);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [isPreviewAccepted, setIsPreviewAccepted] = useState(false);

  const deleteHangingProtocol = useDeleteHangingProtocol({ context: APOLLO_QUERY_CONTEXT });
  const clearSteppedHPState = useClearSteppedHPState();

  const quickUpdate = useSubmitForm({
    hangingProtocol,
    intent: HP_MODAL_VARIANTS.UPDATE,
    currentCase,
    shouldUpdateClassifications: false,
    numberOfPriors,
    includeLayoutChanges: true,
    layouts,
    studyDescriptions,
    groupedStudies: groupStudyIds,
    context: APOLLO_QUERY_CONTEXT,
    onComplete: (hangingProtocolName) => {
      setCompletedMessage(getSuccessNotification(hangingProtocolName, HP_MODAL_VARIANTS.UPDATE));
    },
    onError: (err) => {
      logger.error(err);
      setErrorMessage(getHangingProtocolErrorMessage(err, HP_MODAL_VARIANTS.UPDATE));
    },
  });

  const { invalidateAllPrecachedStudies, refetch } = useBaseViewerData({
    context: APOLLO_QUERY_CONTEXT,
  });

  const { revertAllStepsToBaseline, flushPendingSaves, flushPendingDeletions } =
    useClinicalStepOperations();
  const clinicalEditing = useClinicalViewportEditing();
  const { enqueueToast } = useToasterDispatch();

  const handleResetAll = useCallback(async () => {
    setShowResetModal(false);
    // Start the pending deferred operations, but hold the await until just before the refetch
    // below. Only the server read has to be ordered behind them -- the local reverts that follow
    // are pure state updates, and awaiting first would stall the whole reset UI on a network
    // round-trip.
    //
    // Starting them here rather than after the reverts is deliberate: revertAllStepsToBaseline
    // cancels anything still pending, so reverting first would leave the flush with nothing to
    // send and drop a save the user had already been told succeeded. Both flushes clear their
    // queues synchronously before their first await, so the revert below is a no-op on them.
    const flushed = Promise.all([flushPendingSaves(), flushPendingDeletions()]);
    revertAllStepsToBaseline();
    clinicalEditing.revertAll();
    // Which HP the user has applied, captured before the reset touches anything. A confirmed HP
    // takes priority over the server-matched one (see useHangingProtocol's
    // originalHangingProtocol), and its payload is a snapshot frozen at Confirm time, so leaving it
    // alone would let stale pre-reset data keep winning after the refetch below.
    //
    // Clearing it was the previous answer, and it discarded the user's choice: Reset All on a case
    // where they had selected a different SHP fell through to the server match and silently put the
    // original protocol back (VX-4789). Reset All resets *changes*, not the selection. So refresh
    // the snapshot in place instead -- same smid, freshly fetched below -- which drops the stale
    // payload without changing which HP is applied.
    const confirmedSmidToRefresh = confirmedHangingProtocol?.smid ?? null;
    // Reload HP from server. The flushed writes have to have landed first, or this read can come
    // back with pre-save data and re-seed the step atoms with it, which is what made an
    // already-saved step reappear as unsaved (VX-4740).
    await flushed;
    await invalidateAllPrecachedStudies();
    invalidateHPInitializationCache();
    invalidateHPSmidGuard();
    await refetch();
    // Re-pin after the refetch so the confirmed snapshot is rebuilt from post-flush server data.
    if (confirmedSmidToRefresh != null) {
      await confirmCreatedHangingProtocol(confirmedSmidToRefresh);
    }
    resetToHP();
    broadcastHPInvalidation();
    enqueueToast(RESET_ALL_TOAST.message, {
      ...getDefaultHPToastOptions(),
      ContentComponent: createStepToastContentComponent({}),
    });
  }, [
    flushPendingSaves,
    flushPendingDeletions,
    revertAllStepsToBaseline,
    clinicalEditing,
    confirmedHangingProtocol?.smid,
    confirmCreatedHangingProtocol,
    invalidateAllPrecachedStudies,
    refetch,
    resetToHP,
    enqueueToast,
  ]);

  const shouldOpenHangingProtocolsForm = isSubmitFormIntent(widgetMode);

  // NOTE: This is implemented to where a users viewports configurations and layout are
  // ALWAYS reset after they submit an HP. This is intentional business logic.
  const onHangingProtocolFormSubmit = useCallback(
    async (hangingProtocolName: string, createdHangingProtocolSmid?: string) => {
      // Only drop the init guard when the HP the viewer should be showing actually changes -- a
      // create, or a switch. On a plain update of the HP already applied, the form has just written
      // the saved steps into the viewer atoms itself, and clearing the guard lets the next
      // getBaseViewerData response re-initialize over that write. The form refetches immediately
      // after the mutation, so if the backend has not made the write visible yet that response is
      // the pre-update HP -- and the freshly added steps disappear from the step bar until a reload
      // (VX-4770). The form deliberately does not clear the guard for exactly this reason; the
      // widget was undoing that a level up.
      if (widgetMode !== HP_MODAL_VARIANTS.UPDATE) {
        invalidateHPInitializationCache();
        invalidateHPSmidGuard();
      }
      clearPreviewState();
      // Editing the currently-confirmed HP updates the normalized Apollo cache entity (so
      // matchedHangingProtocol reflects the edit), but confirmedHangingProtocol is a frozen
      // snapshot from Confirm time and would otherwise keep winning over the fresh edit.
      //
      // Refresh that snapshot rather than clearing it. Clearing drops back to
      // `matchedHangingProtocol`, which is not necessarily the same protocol: after a criteria-tie
      // create the match sits on an older same-criteria HP while the confirmed one is the HP the
      // user is editing (VX-4713). Falling through to the match therefore changed the HP smid, and
      // the smid is what keys useHangingProtocol's init guard -- so the guard this PR added stopped
      // applying and the response re-initialized the step bar from the wrong protocol, dropping the
      // steps just added until a reload (VX-4770). Re-pinning the same smid keeps the key stable.
      if (
        hangingProtocol?.smid != null &&
        hangingProtocol.smid === confirmedHangingProtocol?.smid
      ) {
        await confirmCreatedHangingProtocol(hangingProtocol.smid);
      }
      // On CREATE, pin the just-created HP as the confirmed one so it becomes active immediately,
      // even when the server's post-create refetch ties it with an existing same-criteria HP and
      // keeps auto-matching the older one (VX-4713). Mutually exclusive with the UPDATE re-pin
      // above -- a submit is one or the other. Awaited before resetToHP so the reset lands on the
      // new HP's layout, not the stale matched one.
      if (createdHangingProtocolSmid != null) {
        await confirmCreatedHangingProtocol(createdHangingProtocolSmid);
      }
      resetToHP();
      setCompletedMessage(getSuccessNotification(hangingProtocolName, widgetMode));
      close();
    },
    [
      clearPreviewState,
      hangingProtocol?.smid,
      confirmedHangingProtocol?.smid,
      confirmCreatedHangingProtocol,
      resetToHP,
      close,
      widgetMode,
    ]
  );

  const confirmPreviewedHangingProtocol = useCallback(() => {
    setIsPreviewAccepted(true);
    logger.info(`Confirmed Hanging Protocol ${previewedHangingProtocol?.smid}`);
    confirmPreview();
    resetToHP();
    setHighlightRow(null);
    close();
  }, [confirmPreview, resetToHP, close, previewedHangingProtocol?.smid]);

  const cancelPreviewedHangingProtocol = useCallback(() => {
    if (!isPreviewAccepted) {
      rejectAndRemovePreview();
      if (previewedHangingProtocol != null) {
        logger.info(`Rejected Hanging Protocol ${previewedHangingProtocol.smid}`);
      }
    }
    setHighlightRow(null);
    close();
  }, [close, isPreviewAccepted, rejectAndRemovePreview, previewedHangingProtocol]);

  const onHangingProtocolFormError = useCallback(
    (err: Error) => {
      logger.error(err);
      setErrorMessage(getHangingProtocolErrorMessage(err, widgetMode as SubmitFormIntent));
    },
    [widgetMode]
  );

  const handleDeleteClick = useCallback(async () => {
    if (hangingProtocol == null || hangingProtocol.smid == null) {
      return;
    }
    // Held in a local so the narrowing survives into the async callbacks below.
    const deletedHPSmid = hangingProtocol.smid;
    await deleteHangingProtocol({
      variables: { id: deletedHPSmid },
      optimisticResponse: {
        __typename: 'Mutation',
        deleteHangingProtocol: {
          __typename: 'HangingProtocolDeleted',
          smid: hangingProtocol.smid,
        },
      },
      onCompleted: async (data) => {
        if (hangingProtocol?.name == null) {
          return;
        }

        if (isPhase4 && hangingProtocol) {
          analytics.track(viewer.usr.hangingProtocolDeleted, {
            hanging_protocol_name: hangingProtocol.name,
            hanging_protocol_smid: hangingProtocol.smid,
            intent: 'delete',
            hanging_protocol_payload: {
              name: hangingProtocol.name,
              study_descriptions: hangingProtocol.studyDescriptions ?? [],
              number_of_priors: hangingProtocol.numberOfPriors ?? 0,
              study_criteria: hangingProtocol.studyCriteria ?? null,
              layouts: hangingProtocol.data?.layouts ?? [],
              viewports_configurations:
                hangingProtocol.viewportsConfigurations?.map((vc) => ({
                  id: vc.id,
                  view_type: vc.configuration.viewType,
                  series_descriptions: vc.configuration.seriesDescriptions ?? [],
                  series_criteria: vc.configuration.seriesCriteria ?? null,
                  comparison_index: vc.configuration.comparisonIndex,
                })) ?? [],
            },
          });
        }

        setCompletedMessage(`Hanging protocol '${hangingProtocol.name}' deleted successfully.`);
        close();
        // Drop the step state up front rather than leaving each viewer window to work it out from
        // its own refetch. The step atoms sync across windows, so this clears the step bar
        // everywhere immediately, including in a window that has not processed the invalidation
        // broadcast yet and would otherwise keep a stale bar until a refresh (VX-4738).
        clearSteppedHPState();
        // Clearing the atoms is not enough on its own: this window's own in-flight and background
        // queries can still resolve with the deleted HP after the init guard is cleared below, and
        // re-populate the step bar from it. Marking the smid deleted makes those responses be
        // dropped (VX-4738).
        markHangingProtocolDeleted(deletedHPSmid);
        await invalidateAllPrecachedStudies();
        invalidateHPInitializationCache();
        // Deleting the active HP is a legitimate transition to null/default -- clear the
        // smid-write guard so it isn't mistaken for a transient refetch blip and skipped,
        // which would leave activeHangingProtocolSmidState pointing at the deleted HP (VX-4615
        // review).
        invalidateHPSmidGuard();
        await refetch();
        // Signal other viewer windows to refetch so they pick up the deletion. They need the smid
        // too -- each window has its own query lifecycle and its own late responses to drop.
        broadcastHPInvalidation({ deletedHPSmid });
      },
      update(cache, { data }) {
        if (data == null) {
          logger.warn('Unable to delete hanging protocol, missing update data argument');
          return;
        }

        // Reject preview if the deleted hanging protocol matches the deleted one
        if (data.deleteHangingProtocol?.smid === previewedHangingProtocol?.smid) {
          rejectAndRemovePreview();
        }

        // A confirmed HP is no longer backed by a real record once deleted — clear it so
        // originalHangingProtocol falls back to the (refetched) server-matched HP instead of
        // keeping the deleted one applied indefinitely.
        if (data.deleteHangingProtocol?.smid === confirmedHangingProtocol?.smid) {
          clearConfirmedHangingProtocol();
        }
      },
    });
  }, [
    clearSteppedHPState,
    close,
    deleteHangingProtocol,
    hangingProtocol,
    invalidateAllPrecachedStudies,
    refetch,
    isPhase4,
    previewedHangingProtocol?.smid,
    rejectAndRemovePreview,
    confirmedHangingProtocol?.smid,
    clearConfirmedHangingProtocol,
  ]);

  // Type annotation ensures properties aren't inferred as optional.
  // Conditional ensures `widgetMode` is narrowed to `SubmitFormIntent`.
  const hangingProtocolsFormProps: HangingProtocolsFormProps | null = isSubmitFormIntent(widgetMode)
    ? {
        hangingProtocol,
        onSubmit: onHangingProtocolFormSubmit,
        onError: onHangingProtocolFormError,
        onClose: close,
        layouts,
        studyDescriptions,
        groupedStudies: groupStudyIds,
        mode: widgetMode,
        phase,
      }
    : null;

  return (
    <>
      <Tooltip content={hangingProtocol ? 'Hanging Protocol' : null}>
        <Menu
          placement="bottom-end"
          visible={isDropdownOpen}
          onClickOutside={() => {
            setIsDropdownOpen(false);
            close();
          }}
          css={css`
            margin-top: ${Spacings.xsmall}rem;
            margin-bottom: ${Spacings.xsmall}rem;
            opacity: 0.7;
          `}
          appendTo={() => document.body}
          data-testid="hanging-protocol-menu"
          render={() => (
            <>
              <MenuItem css={menuItemCss} onClick={() => {}} disabled>
                <Text color="secondary">Hanging protocols</Text>
              </MenuItem>
              <MenuItem
                title="Browse hanging protocols"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.SEARCH);
                }}
                data-testid="browse-hanging-protocols"
                data-analytics-name="hanging-protocol-browse-button"
              >
                <SearchOutlined css={iconCss} fontSize="small" />
                <Text>Browse</Text>
              </MenuItem>
              <MenuItem
                title="Create new hanging protocol from current viewports"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.SAVE);
                }}
                data-testid="create-hanging-protocols"
                data-analytics-name="hanging-protocol-create-button"
              >
                <AddCircle css={iconCss} fontSize="small" />

                <Text>Create</Text>
              </MenuItem>
              <MenuItem
                title="Edit hanging protocol"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.UPDATE);
                }}
                data-testid="update"
                data-analytics-name="hanging-protocol-update-button"
              >
                <BorderColor css={iconCss} fontSize="small" />
                Edit
              </MenuItem>
              <MenuItem
                title="Reset viewports to hanging protocol defaults"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  setShowResetModal(true);
                }}
                data-testid="reset-hanging-protocol"
                data-analytics-name="hanging-protocol-reset-button"
              >
                <Refresh css={iconCss} fontSize="small" />
                Reset All
              </MenuItem>
              {!isPhase4 && (
                <MenuItem
                  title="Only update hanging protocol layout from current configuration"
                  css={menuItemCss}
                  onClick={(evt) => {
                    setIsDropdownOpen(false);
                    quickUpdate(evt);
                  }}
                  data-testid="update-layout-hanging-protocols"
                  data-analytics-name="hanging-protocol-update-layout-button"
                >
                  <Replay css={iconCss} fontSize="small" />
                  Update layout only
                </MenuItem>
              )}
              <MenuItem
                title="Delete hanging protocol"
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.DELETE);
                }}
                data-testid="delete"
                css={menuItemCss}
              >
                <DeleteForeverIcon css={iconCss} fontSize="small" />
                Delete
              </MenuItem>
            </>
          )}
        >
          <BaseWidget
            data-testid="hanging-protocol-widget"
            placeholder="Hanging Protocol"
            text={!loadingHangingProtocol ? hangingProtocol?.name : 'Loading...'}
            onClick={() => {
              if (isDropdownOpen) {
                setIsDropdownOpen(false);
              } else {
                open(HP_MODAL_VARIANTS.SEARCH);
              }
            }}
            accessories={
              <>
                <AccessoryButton
                  onClick={() => open(HP_MODAL_VARIANTS.SEARCH)}
                  data-testid="hanging-protocol-search"
                  icon={<SearchIcon />}
                />
                {!isDefault(hangingProtocol) && (
                  <AccessoryButton
                    icon={<MoreVert />}
                    data-testid="hanging-protocol-dropdown"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      setIsDropdownOpen((open) => !open);
                    }}
                  />
                )}
                {isDefault(hangingProtocol) && (
                  <Tooltip
                    content={
                      <>
                        New hanging protocol from
                        <br />
                        current viewports
                      </>
                    }
                  >
                    <AccessoryButton
                      onClick={(evt) => {
                        evt.stopPropagation();
                        open(HP_MODAL_VARIANTS.SAVE);
                      }}
                      data-testid="hanging-protocol-save-button"
                      data-analytics-name="hanging-protocol-save-button"
                      icon={<AddCircle />}
                    />
                  </Tooltip>
                )}
              </>
            }
          />
        </Menu>
      </Tooltip>
      {isPhase4 ? (
        <Sidebar
          activeSidebarId={HANGING_PROTOCOL_SIDEBAR_ID}
          isOpen={shouldOpenHangingProtocolsForm && isOpen}
          showOverlay={showSidebarOverlay}
        >
          {hangingProtocolsFormProps != null && (
            <HangingProtocolsFormV4 className="MuiDialog-paper" {...hangingProtocolsFormProps} />
          )}
        </Sidebar>
      ) : phase === HANGING_PROTOCOL_PHASE.THREE ? (
        <Sidebar
          activeSidebarId={HANGING_PROTOCOL_SIDEBAR_ID}
          isOpen={shouldOpenHangingProtocolsForm && isOpen}
          showOverlay={showSidebarOverlay}
        >
          {hangingProtocolsFormProps != null && (
            <HangingProtocolsForm className="MuiDialog-paper" {...hangingProtocolsFormProps} />
          )}
        </Sidebar>
      ) : (
        <Dialog open={shouldOpenHangingProtocolsForm && isOpen} onClose={close}>
          {hangingProtocolsFormProps != null && (
            <HangingProtocolsForm {...hangingProtocolsFormProps} />
          )}
        </Dialog>
      )}
      <DeleteHPModal
        open={widgetMode === HP_MODAL_VARIANTS.DELETE && isOpen}
        protocolName={hangingProtocol?.name ?? ''}
        onClose={close}
        onDelete={handleDeleteClick}
      />
      {showResetModal && (
        <HPConfirmationModal
          {...RESET_ALL_MODAL}
          onPrimaryAction={handleResetAll}
          onSecondaryAction={() => setShowResetModal(false)}
          onClose={() => setShowResetModal(false)}
        />
      )}
      {isOpen && widgetMode === HP_MODAL_VARIANTS.SEARCH && (
        <DraggableContainer
          dragConstraintRef={viewerRef}
          css={css`
            margin-top: 3rem;
          `}
        >
          <Stack
            css={`
              padding: 12px 32px;
              min-width: 458px;
              min-height: 501px;
              border-radius: ${rootTheme.radius.sm}px;
              background-color: ${Colors.gray3};
              color: ${Colors.gray8};
            `}
            data-testid="search-hanging-protocol-modal"
          >
            <Stack
              css={`
                display: flex;
                flex-direction: column;
                justify-content: space-between;
              `}
            >
              <Text variant="display1">Select Hanging Protocol</Text>
              <Text
                css={`
                  padding: 0;
                `}
              >
                {POWERED_BY_SIRONA_CLASSIFICATIONS}
              </Text>
              <Input
                icon={<SearchIcon />}
                placeholder="Enter search term"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="search"
                autoFocus={true}
                css={`
                  margin-top: 18px;
                `}
              />

              <Table
                data={searchItems}
                columns={columns}
                Row={Row}
                // @ts-expect-error Table expects boolean but Row component uses number for index comparison
                highlightRow={highlightRow}
                showHeaderSeparators={true}
                loading={loading}
                css={`
                  background-color: ${Colors.gray1};
                  border-radius: ${rootTheme.radius.sm}px;
                  margin: 18px 0 6px 0;
                  flex: 1;
                  min-height: 0;
                  padding: 10px;
                  max-height: 264px;
                  min-height: 264px;
                `}
              />

              <Text variant="body2">
                Click confirm to keep or cancel to revert to previous settings.
              </Text>

              <Stack css="margin: 24px 0 35px 0; justify-content: flex-end; gap: 18px;">
                <Button
                  onClick={cancelPreviewedHangingProtocol}
                  variant="ghost"
                  data-testid="cancel-previewed-hanging-protocol"
                  disabled={loadingPreview}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-previewed-hanging-protocol"
                  onClick={confirmPreviewedHangingProtocol}
                  variant="primary"
                  disabled={previewedHangingProtocol == null || loadingPreview}
                >
                  Confirm
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </DraggableContainer>
      )}
      <GenericSnackBar
        open={completedMessage != null}
        msg={completedMessage ?? ''}
        handleClose={() => setCompletedMessage(null)}
        severity="default"
        data-testid="success-hanging-protocol"
        ContentProps={SNACKBAR_CONTENT_PROPS}
        icon={completedMessage?.includes('delete') ? 'redDeleteForever' : 'greenCheck'}
      />
      <GenericSnackBar
        open={errorMessage != null}
        msg={errorMessage ?? ''}
        handleClose={unsetError}
        severity="default"
        icon="redWarning"
        data-testid="error-hanging-protocol"
        ContentProps={SNACKBAR_CONTENT_PROPS}
      />
    </>
  );
};

const MemoHangingProtocolWidget = memo(HangingProtocolWidget);

export default MemoHangingProtocolWidget;

export function useShouldShowHangingProtocolWidget() {
  const viewerId = useViewerId();
  return viewerId === '0';
}

HangingProtocolWidget.displayName = 'HangingProtocolWidget';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(HangingProtocolWidget);
